import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAdminListMembers } from '@/hooks';
import { ChevronDown, Search, User, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface UserSelectorProps {
  selectedUserIds: string[]; // Will now contain searchKey values (firstname_surname)
  onSelectionChange: (userIds: string[]) => void;
  placeholder?: string;
}

interface UserOption {
  memberId: string;
  fullName: string;
  initials: string;
  searchKey: string; // firstname_surname format for search
}

export function UserSelector({ 
  selectedUserIds, 
  onSelectionChange, 
  placeholder = "Select users..." 
}: UserSelectorProps) {
  const { data: membersData, isLoading } = useAdminListMembers();
  const members = membersData?.members || [];
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Convert members to user options
  const userOptions: UserOption[] = (members || []).map(member => {
    const firstName = member.firstName || '';
    const surname = member.surname || '';
    const fullName = `${firstName} ${surname}`.trim() || 'Unknown User';
    const initials = `${firstName.charAt(0) || '?'}${surname.charAt(0) || '?'}`.toUpperCase();
    const searchKey = `${firstName}_${surname}`.toLowerCase();
    
    return {
      memberId: member.id.toString(),
      fullName,
      initials,
      searchKey,
    };
  });

  // Filter users based on search term
  const filteredUsers = userOptions.filter(user =>
    user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.memberId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get selected users for display
  const selectedUsers = userOptions.filter(user => 
    selectedUserIds.includes(user.searchKey)
  );

  const handleUserSelect = (searchKey: string) => {
    if (!selectedUserIds.includes(searchKey)) {
      onSelectionChange([...selectedUserIds, searchKey]);
    }
    setSearchTerm('');
  };

  const handleUserRemove = (searchKey: string) => {
    onSelectionChange(selectedUserIds.filter(id => id !== searchKey));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="flex gap-2 w-full">
      {/* User Selector Button - Fixed Width */}
      <div className="relative flex-shrink-0">
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          className="w-48 justify-between"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground truncate">
              {placeholder}
            </span>
          </div>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full left-0 mt-1 w-80 bg-background border rounded-md shadow-lg z-50 max-h-[300px] overflow-hidden"
          >
            {/* Search Input */}
            <div className="p-3 border-b">
              <div className="flex items-center px-2 rounded-md border border-input focus-within:ring-1 focus-within:ring-ring">
                <Search className="h-3.5 w-3.5 text-muted-foreground mr-2" />
                <Input
                  placeholder="Search users..."
                  className="h-8 px-0 border-0 focus-visible:outline-none focus-visible:ring-0 shadow-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* User Options */}
            <div className="max-h-[200px] overflow-y-auto">
              {isLoading ? (
                <div className="p-3 text-sm text-muted-foreground italic">
                  Loading users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground italic">
                  {searchTerm ? 'No users found' : 'No users available'}
                </div>
              ) : (
                filteredUsers.map((user) => (
                  <button
                    key={user.memberId}
                    type="button"
                    className={`w-full flex items-center gap-2 p-3 hover:bg-accent text-left ${
                      selectedUserIds.includes(user.searchKey) 
                        ? 'bg-accent/50 opacity-60' 
                        : ''
                    }`}
                    onClick={() => handleUserSelect(user.searchKey)}
                    disabled={selectedUserIds.includes(user.searchKey)}
                  >
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                      {user.initials}
                    </div>
                    <span className="text-sm flex-1">{user.fullName}</span>
                    {selectedUserIds.includes(user.searchKey) && (
                      <span className="text-xs text-muted-foreground">Selected</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Selected Users Pills - Takes remaining width */}
      <div className="flex-1 flex items-center">
        {selectedUsers.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {selectedUsers.map(user => (
              <Badge 
                key={user.memberId} 
                variant="secondary" 
                className="flex items-center gap-1 pr-1 py-1"
              >
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">
                    {user.initials}
                  </div>
                  <span className="text-xs">{user.fullName}</span>
                </div>
                <button
                  type="button"
                  onClick={() => handleUserRemove(user.searchKey)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
